/**
 * Field-test readiness (iter302).
 *
 * The four prerequisites that silently decide whether the GPS / auto-clock
 * test works at all: shop geofence, a home geofence per tech, coordinates
 * on the sites being dispatched, and a work e-mail on the roster row.
 * One click, plain answers, and a fix button where a fix is possible.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, RefreshCw, MapPin, Check, AlertTriangle, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CollapsibleSection, Callout } from "@/components/settings/SettingsUI";
import { getFieldReadiness, geocodeTechHomes } from "@/lib/api";

const ACCESS_LABEL = {
  active: "App access active", invited: "Invite sent, not used yet",
  revoked: "Access revoked", disabled: "Account disabled",
  jumpcloud: "Signs in with JumpCloud", none: "No app access",
};

const Row = ({ ok, children, testId }) => (
  <div className="flex items-start gap-2 text-sm" data-testid={testId}>
    {ok
      ? <Check className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600" />
      : <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />}
    <div className={ok ? "text-slate-600" : "text-slate-800"}>{children}</div>
  </div>
);

export default function FieldReadinessPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fixing, setFixing] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setData((await getFieldReadiness()).data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not run the check"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const fixHomes = async () => {
    setFixing(true);
    try {
      const { data: r } = await geocodeTechHomes();
      toast.success(`${r.geocoded} home address${r.geocoded === 1 ? "" : "es"} located`
        + (r.failed ? ` · ${r.failed} failed` : "") + (r.skipped ? ` · ${r.skipped} had no address` : ""));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not geocode"); }
    finally { setFixing(false); }
  };

  if (!data) {
    return (
      <CollapsibleSection id="field-readiness" testId="field-readiness-section"
        icon={ShieldCheck} iconClass="text-indigo-600" title="Field test readiness">
        <div className="text-sm text-slate-400 py-2">Running the check…</div>
      </CollapsibleSection>
    );
  }

  const fixableHomes = data.techs.filter(t => t.can_geocode_home);
  const badTechs = data.techs.filter(t => !t.ready);

  return (
    <CollapsibleSection
      id="field-readiness" testId="field-readiness-section"
      icon={ShieldCheck} iconClass="text-indigo-600"
      title="Field test readiness"
      extra={
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-semibold ${
          data.ready ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                     : "bg-amber-50 text-amber-700 border-amber-200"}`}
              data-testid="field-readiness-state">
          {data.ready ? "READY" : `${data.blockers} TO FIX`}
        </span>
      }
      actions={
        <Button size="sm" variant="outline" onClick={load} disabled={loading}
                data-testid="btn-field-readiness-refresh">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          {loading ? "Checking…" : "Re-check"}
        </Button>
      }
    >
      <div className="space-y-4">
        <Callout tone={data.ready ? "success" : "warn"} testId="field-readiness-verdict">
          {data.ready
            ? "Everything the GPS clock needs is in place. Techs who are scheduled will clock in at the shop or their first site and clock out at home."
            : "Fix the flagged items below before the test — each one silently breaks part of the clock rather than throwing an error."}
        </Callout>

        {/* 1 · Shop geofence */}
        <div className="space-y-1.5">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">1 · Shop geofence — clock-in</div>
          <Row ok={data.shop.ready} testId="field-readiness-shop">
            {data.shop.ready
              ? <>Shop set at <strong>{data.shop.address || "coordinates on file"}</strong>, {data.shop.radius_ft} ft radius.</>
              : <>No shop coordinates — <strong>nobody will ever clock in</strong>. Set them in Settings → General → Timekeep Settings (type the address, then Geocode).</>}
          </Row>
          <Row ok testId="field-readiness-window">
            Tracking runs {data.shift_window.start}–{data.shift_window.end} local, and only on days a tech is scheduled.
          </Row>
        </div>

        {/* 2 · Techs */}
        <div className="space-y-1.5 border-t border-slate-100 pt-3">
          <div className="flex items-center justify-between gap-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              2 · Technicians — {data.techs_ready} of {data.techs.length} ready
            </div>
            {fixableHomes.length > 0 && (
              <Button size="sm" variant="outline" onClick={fixHomes} disabled={fixing}
                      data-testid="btn-geocode-tech-homes">
                <Wand2 className="w-3.5 h-3.5 mr-1.5" />
                {fixing ? "Locating…" : `Locate ${fixableHomes.length} home address${fixableHomes.length === 1 ? "" : "es"}`}
              </Button>
            )}
          </div>

          {badTechs.length === 0 ? (
            <Row ok testId="field-readiness-techs-ok">
              Every active tech has a work e-mail, a home geofence and GPS switched on.
            </Row>
          ) : (
            <div className="space-y-2" data-testid="field-readiness-techs">
              {badTechs.map(t => (
                <div key={t.id} className="rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2"
                     data-testid={`field-readiness-tech-${t.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-slate-800">{t.name || "Unnamed tech"}</span>
                    <span className="text-xs text-slate-500">{t.email || "no e-mail"}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-slate-200 bg-white text-slate-500 font-semibold">
                      {ACCESS_LABEL[t.app_access] || t.app_access}
                    </span>
                  </div>
                  <ul className="mt-1 space-y-0.5 pl-1">
                    {t.issues.map((i, idx) => (
                      <li key={idx} className="text-xs text-amber-900">• {i}</li>
                    ))}
                  </ul>
                  {t.can_geocode_home && (
                    <div className="text-[11px] text-slate-500 mt-1">
                      Address on file: {t.home_address} — the Locate button above will fill in the coordinates.
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="text-[11px] text-slate-400">
            Fix e-mails, home addresses and invites in Settings → Users → Technicians / Field access.
            The per-tech GPS switch lives on the Dispatch board.
          </div>
        </div>

        {/* 3 · Sites */}
        <div className="space-y-1.5 border-t border-slate-100 pt-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">
            3 · Sites being dispatched — arrival detection
          </div>
          {data.sites_missing_coords.length === 0 ? (
            <Row ok testId="field-readiness-sites-ok">
              Every site dispatched in the last/next 30 days has coordinates.
              {data.sites_missing_total > 0 && (
                <span className="text-slate-500"> ({data.sites_missing_total} of {data.sites_total} sites
                  elsewhere in the system still have none — harmless until one gets scheduled.)</span>
              )}
            </Row>
          ) : (
            <>
              <Row ok={false} testId="field-readiness-sites">
                <strong>{data.sites_missing_coords.length} scheduled site{data.sites_missing_coords.length === 1 ? "" : "s"}</strong>
                {data.sites_missing_coords.length === 1 ? " has " : " have "}no coordinates — no arrival, no on-site time, no auto status flip.
              </Row>
              <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 overflow-hidden">
                {data.sites_missing_coords.slice(0, 12).map(s => (
                  <div key={s.id} className="px-3 py-2 flex items-start gap-2"
                       data-testid={`field-readiness-site-${s.id}`}>
                    <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-slate-400" />
                    <div className="min-w-0">
                      <div className="text-sm text-slate-800 truncate">{s.name || "Unnamed site"}</div>
                      <div className="text-xs text-slate-500 truncate">{s.address || "no address typed on the site"}</div>
                      {s.work_orders.length > 0 && (
                        <div className="text-[11px] text-slate-400">{s.work_orders.join(", ")}</div>
                      )}
                    </div>
                  </div>
                ))}
                {data.sites_missing_coords.length > 12 && (
                  <div className="px-3 py-2 text-xs text-slate-500">
                    +{data.sites_missing_coords.length - 12} more
                  </div>
                )}
              </div>
              <div className="text-[11px] text-slate-400">
                Run the backfill in Settings → Geocoding ({data.sites_missing_total} sites missing in total).
                Sites with no address typed need the address first.
              </div>
            </>
          )}
        </div>

        {/* 4 · Delivery */}
        <div className="space-y-1.5 border-t border-slate-100 pt-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">4 · Getting the schedule out</div>
          <Row ok={data.dispatch_email.enabled} testId="field-readiness-email">
            {data.dispatch_email.enabled
              ? <>Daily schedule e-mail is ON, {((data.dispatch_email.send_hour_local + 11) % 12) + 1}:00
                  {data.dispatch_email.send_hour_local < 12 ? " AM" : " PM"} on {data.dispatch_email.send_days.length} day
                  {data.dispatch_email.send_days.length === 1 ? "" : "s"} a week.</>
              : <>Daily schedule e-mail is OFF — techs only see their day if they open the app. Turn it on below.</>}
          </Row>
          <Row ok={data.magic_link_enabled} testId="field-readiness-magic">
            {data.magic_link_enabled
              ? "Magic-link sign-in is ON, so invites work."
              : "Magic-link sign-in is OFF — invites and existing phone sessions will not work until an admin turns it on (Settings → Users → Field access)."}
          </Row>
        </div>

        <div className="text-[11px] text-slate-400 border-t border-slate-100 pt-2"
             data-testid="field-readiness-checked-at">
          Checked {new Date(data.checked_at).toLocaleString()}
        </div>
      </div>
    </CollapsibleSection>
  );
}
